import * as React from "react";
import {DEFAULT_TEMPERATURE_UNIT, HaccpController} from "../../HaccpController";
import {unitMapper} from "../HaccpLegend";
import {PossibleContent} from "../HaccpTable";

import "./ConfigurationPopup.scss";
import {VeBoxModal} from "../../../../../components/modal/VeBoxModal";
import {NumberInput, SelectInput, SelectInputOption, SelectInputProps} from "@vebox/etui-core";
import {getLocaleDecimalSeparator} from "../../../../../components/common/etui_components_styles";

export function FormatString(str: string, ...val: string[]) {
    for (let index = 0; index < val.length; index++) {
        str = str.replace(`{${index}}`, val[index]);
    }
    return str;
}

export interface ConfigurationPopupProps {
    texts: {
        title: string,
        content: string,
        maxLabel: string,
        minLabel: string,
        contentTypeLabel: string,
        thresholdLabel: string,
        cancelButtonText: string,
        confirmButtonText: string,
        warning: string,
        popupMinThresholdError: string,
        popupMaxThresholdError: string,
    };
    possibleContents: PossibleContent[];
    defaultContent?: PossibleContent;
    onConfigure: (info: PossibleContent) => void;
    onCancel: () => void;
    showContentType: boolean;
    haccpController: HaccpController;
    equipmentGUID: string;
}

export interface ConfigurationPopupState {
    previousContentIndex: number;
    contentIndex: number;
    min: number;
    max: number;
    errorText: string;
}

const NUMBER_INPUT_STYLE = {
    input: {
        height: 24,
        width: 130,
    },
};

export class ConfigurationPopup extends React.Component<ConfigurationPopupProps, ConfigurationPopupState> {

    constructor(props) {
        super(props);

        const defaultIndex = this.getDefaultContentIndex();

        this.state = {
            previousContentIndex: defaultIndex,
            contentIndex: defaultIndex,
            min: this.props.defaultContent
                ? this.props.defaultContent.minTemperature
                : this.props.possibleContents[defaultIndex].minTemperature,
            max: this.props.defaultContent
                ? this.props.defaultContent.maxTemperature
                : this.props.possibleContents[defaultIndex].maxTemperature,
            errorText: "",
        };
    }

    public render() {
        const contentOptions = this.props.possibleContents.map<SelectInputOption>((content, i) => ({
            value: String(i),
            label: content.name,
        }));
        return (
            <VeBoxModal style={{width: 350}}>
                <VeBoxModal.Header title={this.props.texts.title}/>
                <VeBoxModal.Section>
                    <p className={"paragraph"}>{this.props.texts.content}</p>
                    {
                        this.props.showContentType
                            ? <SelectInput
                                name={"small"}
                                label={this.props.texts.contentTypeLabel}
                                onChange={this.onContentChanged}
                                defaultValue={contentOptions[this.state.previousContentIndex]}
                                options={contentOptions}
                            />
                            : null
                    }
                </VeBoxModal.Section>
                <VeBoxModal.Section>
                    <p className={"paragraph"}>{this.props.texts.thresholdLabel}</p>
                    <div className={"flex"}>
                        <NumberInput
                            name={"min"}
                            value={this.state.min}
                            size="small"
                            label={this.props.texts.minLabel}
                            decimalSeparator={getLocaleDecimalSeparator()}
                            onChange={this.onFieldChange(
                                "min",
                                (value) => Number(value) <= Number(this.state.max),
                                this.props.texts.popupMinThresholdError
                            )}
                            styles={{
                                ...NUMBER_INPUT_STYLE,
                                root: {
                                    mr: 20
                                }
                            }}
                        />
                        <NumberInput
                            name={"max"}
                            value={this.state.max}
                            size="small"
                            label={this.props.texts.maxLabel}
                            decimalSeparator={getLocaleDecimalSeparator()}
                            onChange={this.onFieldChange(
                                "max",
                                (value) => Number(value) >= Number(this.state.min),
                                this.props.texts.popupMaxThresholdError
                            )}
                            styles={NUMBER_INPUT_STYLE}
                        />
                    </div>
                    {this.state.errorText !== "" ? <div className={"field warning"}>
                        <p>{this.state.errorText}</p>
                    </div> : !this.isRecommendedTemperatures()
                        ? (
                            <div className={"field warning"}>
                                <p>* {
                                    this.props.possibleContents[this.state.contentIndex] ?
                                        FormatString(this.props.texts.warning,
                                            this.props.possibleContents[this.state.contentIndex].name,
                                            this.props.possibleContents[this.state.contentIndex].minTemperature
                                                .toFixed(1),
                                            this.props.possibleContents[this.state.contentIndex].maxTemperature
                                                .toFixed(1),
                                            unitMapper[DEFAULT_TEMPERATURE_UNIT],
                                            unitMapper[DEFAULT_TEMPERATURE_UNIT]) : ""
                                }
                                </p>
                            </div>
                        ) : null
                    }
                </VeBoxModal.Section>
                <VeBoxModal.StandardActions
                    /*submitText={this.props.texts.confirmButtonText}
                    cancelText={this.props.texts.cancelButtonText}*/
                    onSubmit={this.onConfigure}
                    onCancel={this.props.onCancel}
                />
            </VeBoxModal>
        );
    }

    private getDefaultContentIndex() {
        if (this.props.defaultContent) {
            const currentContentIndex = this.props.possibleContents.findIndex(
                (elem) => elem.guid === this.props.defaultContent.guid);
            if (currentContentIndex >= 0) {
                return currentContentIndex;
            } else {
                const otherContent = this.props.possibleContents.findIndex(
                    (elem) => elem.guid === "51d6d642-caf5-11e8-9c0a-9418826ed820");
                if (otherContent) return otherContent;
            }
        }
        return 0; // returns first element of list as a fallback
    }

    private isRecommendedTemperatures = (): boolean => {
        return this.props.possibleContents[this.state.contentIndex].maxTemperature === this.state.max
            && this.props.possibleContents[this.state.contentIndex].minTemperature === this.state.min;
    }

    private indexOfContent = (content: PossibleContent) => {
        return this.props.possibleContents
            .findIndex((elem) => elem.guid === content.guid);
    }

    private onConfigure = (_) => {
        if (this.props.equipmentGUID) {
            this.props.haccpController.equipmentGUID = this.props.equipmentGUID;
        }
        this.props.onConfigure(
            {
                ...this.props.possibleContents[this.state.contentIndex],
                maxTemperature: this.state.max,
                minTemperature: this.state.min,
            },
        );
    }

    private onContentChanged: SelectInputProps['onChange'] = (onChangeValue) => {
        this.setState({
            contentIndex: onChangeValue["value"],
            max: this.props.possibleContents[onChangeValue["value"]].maxTemperature,
            min: this.props.possibleContents[onChangeValue["value"]].minTemperature,
        });
    }

    private onFieldChange = (field: string, condition?: (value) => boolean, unsatisfiedConditionMessage?: string) => {
        return (value) => {
            // const value = e.target.value;
            if (!condition || (condition && condition(value))) {
                this.setState({
                    ...this.state,
                    [field]: value,
                    errorText: ""
                });
            } else {
                this.setState({
                    errorText: unsatisfiedConditionMessage,
                })
            }
        };
    }
}
